import { FormEvent, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBindings, useCreateBinding } from '@/api/hooks';
import { ErrorCard } from '@/components/ErrorCard';
import { JsonView } from '@/components/JsonView';
import { SkeletonRow } from '@/components/SkeletonRow';
import { StatusBadge } from '@/components/StatusBadge';
import { PageHeader, Panel, PrimaryButton, Select, Td, TextInput, Th } from './pageParts';

const initialForm = {
  tenant_key: '',
  application_id: '',
  source_type: 'group',
  source_id: '',
  role_id: '',
  note: '',
  dry_run: false,
};

export function BindingsPage() {
  const [tenant, setTenant] = useState('');
  const [app, setApp] = useState('');
  const [message, setMessage] = useState<unknown>(null);
  const [form, setForm] = useState(initialForm);
  const queryClient = useQueryClient();
  const bindings = useBindings({ tenant_key: tenant, application_id: app });
  const create = useCreateBinding();
  const ready = Boolean(tenant || app);

  function submit(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    create.mutate(
      { ...form, note: form.note || null },
      {
        onSuccess: (data) => {
          setMessage(data);
          void queryClient.invalidateQueries({ queryKey: ['admin-bindings'] });
        },
        onError: (err) => setMessage({ error: err.message }),
      },
    );
  }

  return (
    <div className="flex flex-col gap-panel-gap">
      <PageHeader title="Bindings" subtitle="先选择 tenant_key 或 application_id 再拉取绑定；create 成功后后端仅 fire-and-forget 触发同步。" />
      <Panel title="List Filter">
        <div className="flex flex-wrap gap-stack-md">
          <TextInput aria-label="list tenant_key" value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="tenant_key" />
          <TextInput aria-label="list application_id" value={app} onChange={(e) => setApp(e.target.value)} placeholder="application_id" />
        </div>
        {!ready ? <p className="mt-stack-sm font-body-sm text-body-sm text-on-surface-variant">请选择 tenant_key 或 application_id 后再查询。</p> : null}
      </Panel>
      {ready ? bindings.isPending ? (
        <Panel><SkeletonRow count={5} cells={6} /></Panel>
      ) : bindings.error ? (
        <ErrorCard title="Bindings 请求失败" status={bindings.error.status} body={bindings.error.body} />
      ) : (
        <Panel title={`Bindings · ${bindings.data?.count ?? 0}`}>
          <table className="w-full border-collapse text-left">
            <thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>tenant</Th><Th>app</Th><Th>source</Th><Th>role</Th><Th>enabled</Th><Th>note</Th></tr></thead>
            <tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">
              {(bindings.data?.bindings ?? []).map((b) => <tr key={b.id}><Td>{b.tenant_key}</Td><Td>{b.application_id}</Td><Td>{b.source_type}:{b.source_id}</Td><Td>{b.role_id}</Td><Td><StatusBadge variant={b.enabled ? 'success' : 'danger'}>{String(b.enabled)}</StatusBadge></Td><Td>{b.note ?? b.orphan_reason ?? '-'}</Td></tr>)}
            </tbody>
          </table>
        </Panel>
      ) : null}
      <Panel title="Create Binding">
        <form onSubmit={submit} className="grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <TextInput aria-label="create tenant_key" required placeholder="tenant_key" value={form.tenant_key} onChange={(e) => setForm({ ...form, tenant_key: e.target.value })} />
          <TextInput aria-label="create application_id" required placeholder="application_id" value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })} />
          <Select aria-label="create source_type" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })}>
            <option value="group">group</option>
            <option value="tenant">tenant</option>
          </Select>
          <TextInput aria-label="create source_id" required placeholder="source_id" value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })} />
          <TextInput aria-label="create role_id" required placeholder="role_id" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} />
          <TextInput aria-label="create note" placeholder="note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <label className="flex items-center gap-stack-sm font-body-sm text-body-sm">
            <input type="checkbox" checked={form.dry_run} onChange={(e) => setForm({ ...form, dry_run: e.target.checked })} />
            dry_run only
          </label>
          <PrimaryButton type="submit" disabled={create.isPending}>{form.dry_run ? 'Dry-run Binding' : 'Create Binding'}</PrimaryButton>
        </form>
        {message ? <div className="mt-stack-md"><JsonView value={message} /></div> : null}
      </Panel>
    </div>
  );
}
